import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AuthenticationService } from '../../gt2/api/auth-service.service';
import { SessionEventsService } from '../../gt2/api/session-events.service';
import {
  ChatConversation,
  ChatConversationTouchedEvent,
  ChatMessage,
  ChatMessageDeletedEvent,
  ChatParticipantsChangedEvent,
  ChatReactionAddedEvent,
  ChatReactionRemovedEvent,
  ChatUserSummary,
} from '../../main/apps/chat/models/chat.models';
import { ReverbService } from '../reverb.service';

export interface ChatTypingEvent {
  conversation_uuid: string;
  id: string; // user uuid
  name: string;
}

export interface ChatPresenceMember {
  id: string;
  uuid: string;
  name: string;
  avatar_thumb: string | null;
}

/**
 * Couche socket du chat d'équipe — réutilise l'instance Echo PARTAGÉE de
 * ReverbService (un seul websocket avec le widget IA). Channels backend T3 :
 *   chat.{tenantSlug}.user.{userUuid}          (abonnement permanent — liste)
 *   chat.{tenantSlug}.conversation.{uuid}      (private + presence appariés,
 *                                               REFCOUNT : n composants ouverts
 *                                               sur le même fil = 1 subscription)
 * tenantSlug = localStorage 'PREFIX' (même source que le header App-Tenant).
 */
@Injectable({ providedIn: 'root' })
export class ChatSocketService {
  private userChannelName: string | null = null;
  private onlineChannelName: string | null = null;
  private conversationRefCounts = new Map<string, number>();
  /** Presence channels par conversation — porteurs des whispers typing (T6). */
  private presenceChannels = new Map<string, { whisper: (event: string, data: unknown) => unknown }>();
  private lastTypingWhisperAt = new Map<string, number>();
  private refreshingAuth = false;

  private static readonly TYPING_THROTTLE_MS = 2000;

  // ---- Événements du fil (canal conversation) ----
  public messageSent$ = new Subject<ChatMessage>();
  public messageUpdated$ = new Subject<ChatMessage>();
  public messageDeleted$ = new Subject<ChatMessageDeletedEvent>();
  public messagePinned$ = new Subject<ChatMessage>();
  public messageUnpinned$ = new Subject<ChatMessage>();
  public reactionAdded$ = new Subject<ChatReactionAddedEvent>();
  public reactionRemoved$ = new Subject<ChatReactionRemovedEvent>();

  // ---- Événements de liste (canal user; participants.changed arrive aussi
  //      sur le canal conversation — les mutations du store sont idempotentes) ----
  public conversationTouched$ = new Subject<ChatConversationTouchedEvent>();
  public conversationUpdated$ = new Subject<ChatConversation>();
  public conversationCreated$ = new Subject<ChatConversation>();
  public participantsChanged$ = new Subject<ChatParticipantsChangedEvent>();

  // ---- Typing (whispers, zéro backend) + présence tenant-wide (T6) ----
  public typing$ = new Subject<ChatTypingEvent>();
  public typingStopped$ = new Subject<{ conversation_uuid: string; id: string }>();
  public onlineHere$ = new Subject<ChatPresenceMember[]>();
  public onlineJoining$ = new Subject<ChatPresenceMember>();
  public onlineLeaving$ = new Subject<ChatPresenceMember>();

  /** Relais du signal de reconnexion pusher (gap-fill dans le store). */
  public readonly reconnected$: Observable<void>;

  constructor(
    private reverb: ReverbService,
    private authService: AuthenticationService,
    private sessionEvents: SessionEventsService,
  ) {
    this.reconnected$ = this.reverb.reconnected$.asObservable();
    this.sessionEvents.logout$.subscribe(() => this.teardown());
  }

  private tenantSlug(): string | null {
    return localStorage.getItem('PREFIX');
  }

  /** Abonnement permanent au canal personnel — appelé à l'ouverture du chat. */
  public init(userUuid: string): void {
    const slug = this.tenantSlug();
    const echo = this.reverb.ensureConnected();
    if (!echo || !slug || this.userChannelName !== null) {
      return;
    }

    const name = `chat.${slug}.user.${userUuid}`;
    this.userChannelName = name;

    echo
      .private(name)
      .listen('.conversation.touched', (e: ChatConversationTouchedEvent) => this.conversationTouched$.next(e))
      .listen('.conversation.updated', (e: ChatConversation) => this.conversationUpdated$.next(e))
      .listen('.conversation.created', (e: ChatConversation) => this.conversationCreated$.next(e))
      .listen('.participants.changed', (e: ChatParticipantsChangedEvent) => this.participantsChanged$.next(e))
      .error((status: unknown) =>
        this.handleChannelAuthError(status, () => {
          echo.leave(name);
          this.userChannelName = null;
          this.init(userUuid);
        }),
      );

    // Presence tenant-wide (pastilles en ligne) — permanent, comme le canal user.
    const onlineName = `chat.${slug}.online`;
    this.onlineChannelName = onlineName;
    echo
      .join(onlineName)
      .here((members: ChatPresenceMember[]) => this.onlineHere$.next(members))
      .joining((member: ChatPresenceMember) => this.onlineJoining$.next(member))
      .leaving((member: ChatPresenceMember) => this.onlineLeaving$.next(member));
  }

  public subscribeConversation(conversationUuid: string): void {
    const current = this.conversationRefCounts.get(conversationUuid) ?? 0;
    this.conversationRefCounts.set(conversationUuid, current + 1);
    if (current > 0) {
      return; // déjà abonné (refcount)
    }

    const slug = this.tenantSlug();
    const echo = this.reverb.ensureConnected();
    if (!echo || !slug) {
      return;
    }

    const name = `chat.${slug}.conversation.${conversationUuid}`;

    echo
      .private(name)
      .listen('.message.sent', (e: ChatMessage) => this.messageSent$.next(e))
      .listen('.message.updated', (e: ChatMessage) => this.messageUpdated$.next(e))
      .listen('.message.deleted', (e: ChatMessageDeletedEvent) => this.messageDeleted$.next(e))
      .listen('.message.pinned', (e: ChatMessage) => this.messagePinned$.next(e))
      .listen('.message.unpinned', (e: ChatMessage) => this.messageUnpinned$.next(e))
      .listen('.message.reaction.added', (e: ChatReactionAddedEvent) => this.reactionAdded$.next(e))
      .listen('.message.reaction.removed', (e: ChatReactionRemovedEvent) => this.reactionRemoved$.next(e))
      .listen('.participants.changed', (e: ChatParticipantsChangedEvent) => this.participantsChanged$.next(e))
      .listen('.conversation.updated', (e: ChatConversation) => this.conversationUpdated$.next(e))
      .error((status: unknown) =>
        this.handleChannelAuthError(status, () => {
          echo.leave(name);
          const count = this.conversationRefCounts.get(conversationUuid) ?? 0;
          if (count > 0) {
            this.conversationRefCounts.set(conversationUuid, count - 1);
            this.subscribeConversation(conversationUuid);
          }
        }),
      );

    // Presence apparié — porteur des whispers typing (T6, zéro backend).
    const presence = echo
      .join(name)
      .listenForWhisper('typing', (e: { id: string; name: string }) =>
        this.typing$.next({ conversation_uuid: conversationUuid, id: e.id, name: e.name }),
      )
      .listenForWhisper('stopped', (e: { id: string }) =>
        this.typingStopped$.next({ conversation_uuid: conversationUuid, id: e.id }),
      );
    this.presenceChannels.set(conversationUuid, presence as unknown as { whisper: (event: string, data: unknown) => unknown });
  }

  /** Whisper typing — THROTTLE 2 s par conversation (max 1 émission / 2 s). */
  public whisperTyping(conversationUuid: string, payload: { id: string; name: string }): void {
    const channel = this.presenceChannels.get(conversationUuid);
    if (!channel) {
      return;
    }

    const now = Date.now();
    const last = this.lastTypingWhisperAt.get(conversationUuid) ?? 0;
    if (now - last < ChatSocketService.TYPING_THROTTLE_MS) {
      return;
    }
    this.lastTypingWhisperAt.set(conversationUuid, now);
    channel.whisper('typing', payload);
  }

  /** Clear immédiat côté destinataires à l'envoi du message. */
  public whisperStopped(conversationUuid: string, payload: { id: string }): void {
    this.presenceChannels.get(conversationUuid)?.whisper('stopped', payload);
    this.lastTypingWhisperAt.delete(conversationUuid);
  }

  public unsubscribeConversation(conversationUuid: string): void {
    const current = this.conversationRefCounts.get(conversationUuid) ?? 0;
    if (current > 1) {
      this.conversationRefCounts.set(conversationUuid, current - 1);
      return;
    }

    this.conversationRefCounts.delete(conversationUuid);
    this.presenceChannels.delete(conversationUuid);
    this.lastTypingWhisperAt.delete(conversationUuid);
    const slug = this.tenantSlug();
    const echo = this.reverb.ensureConnected();
    if (echo && slug) {
      // leave() couvre les variantes private- ET presence- du même nom.
      echo.leave(`chat.${slug}.conversation.${conversationUuid}`);
    }
  }

  /**
   * Échec d'auth d'un channel (token expiré) : un refresh via le mécanisme
   * existant (AuthenticationService.refreshToken, celui du JwtInterceptor),
   * puis re-subscription. Un seul refresh concurrent.
   */
  private handleChannelAuthError(status: unknown, resubscribe: () => void): void {
    const httpStatus = typeof status === 'number' ? status : (status as { status?: number })?.status;
    if (httpStatus !== 401 && httpStatus !== 403) {
      return;
    }
    if (this.refreshingAuth) {
      return;
    }

    this.refreshingAuth = true;
    this.authService.refreshToken().subscribe({
      next: () => {
        this.refreshingAuth = false;
        resubscribe();
      },
      error: () => {
        this.refreshingAuth = false;
      },
    });
  }

  public teardown(): void {
    const slug = this.tenantSlug();
    const echo = this.reverb.ensureConnected();
    if (echo && slug) {
      if (this.userChannelName) {
        echo.leave(this.userChannelName);
      }
      if (this.onlineChannelName) {
        echo.leave(this.onlineChannelName);
      }
      for (const uuid of this.conversationRefCounts.keys()) {
        echo.leave(`chat.${slug}.conversation.${uuid}`);
      }
    }
    this.userChannelName = null;
    this.onlineChannelName = null;
    this.conversationRefCounts.clear();
    this.presenceChannels.clear();
    this.lastTypingWhisperAt.clear();
    // La déconnexion Echo elle-même est gérée par ReverbService (logout$).
  }
}
